// Shared building blocks for every portal page. Read design/README.md before adding a page.
import { createContext, createElement, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Info, WarningCircle, X } from '@phosphor-icons/react';

function cx(...names) {
  return names.filter(Boolean).join(' ');
}

/** Page container: max width, side gutters that clear phone notches, consistent vertical rhythm. */
export function Page({ as = 'div', width, className, children, ...rest }) {
  return createElement(as, {
    ...rest,
    className: cx('rc-page', width === 'narrow' && 'rc-page--narrow', width === 'bleed' && 'rc-page--bleed', className),
  }, children);
}

/** The part of the portal the current page belongs to (Slabs, Stock room...). The frame provides it. */
export const SectionContext = createContext(null);

/**
 * Page title block. `back` is { to, label } for pages that sit under another page.
 * The red label above the title names the menu section the page sits in; pass `eyebrow` to
 * change it or `eyebrow={false}` to hide it.
 */
export function PageHeader({ title, description, actions, back, eyebrow, children }) {
  const section = useContext(SectionContext);
  const label = eyebrow === false ? null : eyebrow || section;
  return (
    <header className="rc-page-header">
      <div className="rc-page-header__text">
        {back ? (
          <Link className="rc-page-back" to={back.to}>
            <ArrowLeft size={16} aria-hidden="true" />
            {back.label}
          </Link>
        ) : null}
        {label && title ? <p className="rc-page-eyebrow">{label}</p> : null}
        {title ? <h1 className="rc-page-title">{title}</h1> : null}
        {description ? <p className="rc-page-description">{description}</p> : null}
        {children}
      </div>
      {actions ? <div className="rc-page-actions">{actions}</div> : null}
    </header>
  );
}

/** A surface for one group of content. Use sparingly: only where grouping needs elevation. */
export function Panel({ as = 'section', title, meta, actions, inset, flush, className, children, ...rest }) {
  const head = title || meta || actions ? (
    <div className="rc-panel__head">
      <div className="rc-stack" style={{ gap: 2 }}>
        {title ? <h2 className="rc-panel__title">{title}</h2> : null}
        {meta ? <span className="rc-panel__meta">{meta}</span> : null}
      </div>
      {actions ? <div className="rc-row">{actions}</div> : null}
    </div>
  ) : null;
  return createElement(as, {
    ...rest,
    className: cx('rc-panel', inset && 'rc-panel--inset', flush && 'rc-panel--flush', className),
  }, head, children);
}

const BUTTON_VARIANTS = { primary: 'rc-btn--primary', secondary: '', danger: 'rc-btn--danger', ghost: 'rc-btn--ghost' };

/** Buttons: one primary action per view. Labels say exactly what happens ("Save prices", not "Submit"). */
export function Button({ as, to, href, variant = 'secondary', size, icon, iconRight, block, iconOnly, className, children, type, ...rest }) {
  const element = as || (to ? Link : href ? 'a' : 'button');
  return createElement(element, {
    ...rest,
    to,
    href,
    type: element === 'button' ? type || 'button' : undefined,
    className: cx('rc-btn', BUTTON_VARIANTS[variant], size === 'sm' && 'rc-btn--sm', block && 'rc-btn--block', iconOnly && 'rc-btn--icon', className),
  }, icon, iconOnly ? null : children, iconRight);
}

export function Badge({ tone, className, children, ...rest }) {
  return <span {...rest} className={cx('rc-badge', tone && `rc-badge--${tone}`, className)}>{children}</span>;
}

const ALERT_ICONS = { success: CheckCircle, error: WarningCircle, warning: WarningCircle, info: Info };

/** Inline message. Say what happened and what to do next; no exclamation marks. */
export function Alert({ tone = 'info', title, onClose, className, children, ...rest }) {
  const Icon = ALERT_ICONS[tone] || Info;
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} {...rest} className={cx('rc-alert', `rc-alert--${tone}`, className)}>
      <Icon size={20} weight="regular" aria-hidden="true" />
      <div className="rc-alert__body">
        {title ? <strong>{title}</strong> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {onClose ? (
        <button type="button" className="rc-alert__close" onClick={onClose} aria-label="Dismiss">
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** A composed empty view: what will appear here and how to get started. */
export function EmptyState({ icon: Icon, title, children, actions, className }) {
  return (
    <div className={cx('rc-empty', className)}>
      {Icon ? <span className="rc-empty__icon"><Icon size={20} aria-hidden="true" /></span> : null}
      <h2 className="rc-empty__title">{title}</h2>
      {children ? <p className="rc-empty__body">{children}</p> : null}
      {actions ? <div className="rc-row">{actions}</div> : null}
    </div>
  );
}

/** Skeleton shaped like the content that is loading. */
export function Skeleton({ width = '100%', height = 12, radius, className, style }) {
  return <span aria-hidden="true" className={cx('rc-skeleton', className)} style={{ width, height, borderRadius: radius, ...style }} />;
}

export function SkeletonRows({ rows = 4, label = 'Loading' }) {
  return (
    <div className="rc-stack" style={{ gap: 12 }} role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="rc-row" style={{ gap: 16, flexWrap: 'nowrap' }}>
          <Skeleton width={40} height={40} radius={8} />
          <div className="rc-stack" style={{ gap: 8, flex: 1 }}>
            <Skeleton width={`${62 - index * 7}%`} height={12} />
            <Skeleton width={`${38 - index * 4}%`} height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Stat({ label, value, note, className }) {
  return (
    <div className={cx('rc-stat', className)}>
      <span className="rc-stat__label">{label}</span>
      <span className="rc-stat__value">{value}</span>
      {note ? <span className="rc-stat__note">{note}</span> : null}
    </div>
  );
}

export function Stats({ children }) {
  return <div className="rc-stats">{children}</div>;
}

export function TableWrap({ children, className }) {
  return <div className={cx('rc-table-wrap', className)}>{children}</div>;
}
